import { Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ProposalTableContext } from "work/proposals/_views/proposal-table-context";

type ProposalTableContextStyleConfig = {
  label: string;
  palette: "warning" | "info" | "text";
  backgroundOpacity: number;
  hoverOpacity: number;
  selectedOpacity: number;
  selectedHoverOpacity: number;
};

// The rows and the move chips above the table both read this, so a tint and the chip that names it
// cannot drift apart (#6473). Your move is amber rather than red: the row is the viewer's turn, not a
// failure, and red is kept for the rows that could not be loaded at all. A row that asks nothing of
// the viewer is not tinted, so the tinted rows are the ones to look at.
export const proposalTableContextStyleConfig: Record<ProposalTableContext, ProposalTableContextStyleConfig> = {
  [ProposalTableContext.YourMove]: {
    label: "Your move",
    palette: "warning",
    backgroundOpacity: 0.12,
    hoverOpacity: 0.18,
    selectedOpacity: 0.22,
    selectedHoverOpacity: 0.28,
  },
  [ProposalTableContext.YourReview]: {
    label: "Your review",
    palette: "info",
    backgroundOpacity: 0.1,
    hoverOpacity: 0.16,
    selectedOpacity: 0.2,
    selectedHoverOpacity: 0.26,
  },
  [ProposalTableContext.NoAction]: {
    label: "No action needed",
    palette: "text",
    backgroundOpacity: 0,
    hoverOpacity: 0.04,
    selectedOpacity: 0.08,
    selectedHoverOpacity: 0.11,
  },
};

type ProposalTableContextRowStyles = {
  backgroundColor?: string;
  [selector: string]: unknown;
};

export function getProposalTableContextRowStyles(
  theme: Theme,
  context?: ProposalTableContext,
): ProposalTableContextRowStyles {
  if (!context) return {};

  const config = proposalTableContextStyleConfig[context];
  const contextColor = getProposalTableContextColor(theme, context);

  return {
    backgroundColor: alpha(contextColor, config.backgroundOpacity),
    "& > td": {
      backgroundColor: "inherit",
    },
    "&:hover": {
      backgroundColor: alpha(contextColor, config.hoverOpacity),
    },
    "&.Mui-selected": {
      backgroundColor: alpha(contextColor, config.selectedOpacity),
    },
    "&.Mui-selected:hover": {
      backgroundColor: alpha(contextColor, config.selectedHoverOpacity),
    },
  };
}

export function getProposalTableContextBackground(theme: Theme, context: ProposalTableContext): string {
  return alpha(getProposalTableContextColor(theme, context), proposalTableContextStyleConfig[context].backgroundOpacity);
}

/**
 * The swatch a move chip leads with: the tint of the rows it lists, strong enough to read at chip
 * size, edged in the full colour. A row with no tint gets an empty swatch with a plain edge.
 */
export function getProposalTableContextSwatch(
  theme: Theme,
  context: ProposalTableContext,
): { backgroundColor: string; borderColor: string } {
  const color = getProposalTableContextColor(theme, context);
  if (context === ProposalTableContext.NoAction) {
    return { backgroundColor: "transparent", borderColor: theme.palette.text.disabled };
  }
  return { backgroundColor: alpha(color, 0.4), borderColor: color };
}

function getProposalTableContextColor(theme: Theme, context: ProposalTableContext): string {
  const { palette } = proposalTableContextStyleConfig[context];
  if (palette === "text") return theme.palette.text.primary;
  return theme.palette[palette].main;
}
